import { handle } from 'redux-pack';
import { Action } from "../../../types/actions";
import { IChatPromotion, ISocketCommentProps, Status } from '../../../types/working-model';
import { toMap } from '../../../utils/utils';
import {
	CLEAR_CHAT_MESSAGES,
	DELETE_CHAT_MESSAGE,
	INCOMING_CHAT_MESSAGE,
	LOAD_CHAT_MESSAGES,
	SET_CHAT_PROMOTION,
	UNAPPROVE_CHAT_MESSAGE,
	UNDELETE_CHAT_MESSAGE,
	TOGGLE_CHAT_PANEL,
} from '../../actions/event/chat-actions';

export interface ChatState {
	comments: Map<number, ISocketCommentProps>;
	loadingComments: boolean;
	loadingCommentsError: string;
	count: number;
	chatPromotion: IChatPromotion | null;
	isChatPanelOpen: boolean;
}

const defaultState: ChatState = {
	comments: new Map(),
	loadingComments: false,
	loadingCommentsError: "",
	count: 0,
	chatPromotion: null,
	isChatPanelOpen: true,
};

export default function ChatReducer(state: ChatState = defaultState, action: Action): ChatState {
	switch (action.type) {
		case UNDELETE_CHAT_MESSAGE:
		case INCOMING_CHAT_MESSAGE: {
			const existed = state.comments.has(action.payload.id);
			const comments = new Map(state.comments);
			comments.set(action.payload.id, action.payload);
			let updatedCount = state.count;

			// we are allowing banned messages through to be displayed to sender
			const allowed = [Status.Approved, Status.Banned].includes(action.payload?.status);

			if (!action.payload?.spam && allowed) {
				// users receive their own messages directly and from WS - don't increment if this message was already in the map
				if (!existed) {
					updatedCount += 1;
				}
			} else {
				updatedCount -= 1;
			}

			return {
				...state,
				count: updatedCount < 0 ? 0 : updatedCount,
				comments: comments
			};
		}

		case DELETE_CHAT_MESSAGE:
		case UNAPPROVE_CHAT_MESSAGE: {
			const comments = new Map(state.comments);
			comments.delete(action.payload);
			const updatedCount = state.count - 1;
			return {
				...state,
				count: updatedCount < 0 ? 0 : updatedCount,
				comments: comments
			};
		}

		case LOAD_CHAT_MESSAGES: {
			return handle(state, action, {
				start: (state) => ({ ...state, loadingComments: true }),
				finish: (state) => ({ ...state, loadingComments: false }),
				failure: (state) => ({ ...state, loadingCommentsError: 'Unable to load chat' }),
				success: (state) => {
					if ('error' in action.payload) {
						return ({
							...state,
							loadingCommentsError: action.payload.error
						});
					} else if ('msg' in action.payload) {
						return ({
							...state,
							loadingCommentsError: action.payload.msg
						});
					} else {
						return {
							...state,
							loadingCommentsError: '',
							comments: toMap('id', action?.payload?.messages ?? []) as Map<number, ISocketCommentProps>,
							count: action.payload.count,
						};
					}
				}
			});
		}
		case SET_CHAT_PROMOTION: {
			return {
				...state,
				chatPromotion: action.payload
			};
		}

		case CLEAR_CHAT_MESSAGES: {
			return {
				...state,
				loadingCommentsError: '',
				comments: new Map(),
				count: 0,
			};
		}

		case TOGGLE_CHAT_PANEL: {
			return {
				...state,
				isChatPanelOpen: action.payload,
			};
		}

		default: return state;
	}
}